import { isDetailColorId } from '@aboutyou/legacy-shared/src/utils/detailColor'
import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { FC, useMemo } from 'react'
import { SlugifiedAttributesFilterItemWithValues } from 'services/providers/bapi/entities/filters/normalize'
import {
  ColorFilter as ColorFilterType,
  sortColorsFilterComparator,
} from 'sharedComponents/ProductStream/filters/ColorFilters'
import { useAttributesFilterProps } from 'sharedComponents/ProductStream/filters/useAttributesFilterProps'
import styled from 'styled-components'
import { HeadingXXS } from 'uiComponents/atoms/Text/Headings'
import { darkGrayColor, primaryColor } from 'uiComponents/constants/colors'
import { fwMediumBold } from 'uiComponents/constants/typography'
import ColorCheckbox from 'uiComponents/molecules/ColorCheckbox/ColorCheckbox'
import UnfoldingFilterBox from 'uiComponents/organisms/Filters/UnfoldingFilterBox'
import {
  useTrackOnCloseFilter,
  useTrackOnOpenFilter,
} from '../../../../helpers/tracking/hooks/trackFilter'

const Colors = styled.ul`
  list-style-type: none;
  display: flex;
  flex-wrap: wrap;
`

const ColorBubble = styled.label`
  display: flex;
  flex: 50%;
  align-items: center;
  max-width: 50%;
  margin-bottom: 12px;
`

const ColorName = styled(HeadingXXS)<{
  highlighted: boolean
}>`
  color: ${props => (props.highlighted ? primaryColor : darkGrayColor)};
  margin-left: 12px;
  text-transform: capitalize;
  ${fwMediumBold};
`

const ColorFilter: FC<{
  filter: SlugifiedAttributesFilterItemWithValues
}> = ({ filter }) => {
  const { t } = useTranslation()
  const { options, selectedValues, toggleSelection, onReset } =
    useAttributesFilterProps(filter)
  const onCloseFilterTracking = useTrackOnCloseFilter()
  const onOpenFilterTracking = useTrackOnOpenFilter()
  const trackOnClose = () => {
    onCloseFilterTracking(filter.slug, 'quick_filters')
  }
  const trackOnOpen = () => {
    onOpenFilterTracking(filter.slug, 'quick_filters')
  }
  const sortedColors = useMemo(
    () =>
      options
        .map(value => ({
          id: value.id,
          name: value.name,
          value: Number(value.id),
        }))
        .filter((item): item is ColorFilterType => isDetailColorId(item.value))
        .sort(sortColorsFilterComparator),
    [options],
  )

  return (
    <UnfoldingFilterBox
      dataTestId="Color"
      label={t('Farbe')}
      activeFiltersCount={selectedValues.length}
      onReset={onReset}
      styledContentStyles={{ padding: '22px 24px 10px' }}
      onClose={trackOnClose}
      onOpen={trackOnOpen}
    >
      <Colors data-testid="Colors">
        {sortedColors.map(color => {
          const { id } = color
          const checked = selectedValues.includes(id)
          return (
            <ColorBubble data-testid={'Color_' + id} key={id} htmlFor={id}>
              <ColorCheckbox
                checked={checked}
                colorId={color.value}
                id={id}
                onChange={() => toggleSelection(id)}
              />
              <ColorName
                data-testid={'Name_' + color.name}
                highlighted={checked}
              >
                {color.name}
              </ColorName>
            </ColorBubble>
          )
        })}
      </Colors>
    </UnfoldingFilterBox>
  )
}

export default ColorFilter
